import { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid2 as Grid,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import UpdateStatusButton from './UpdateStatusButton';
import { COLORS, DATE_FORMATS } from '../../../utils/Application_Constants';
import { formatUtcDate } from '../../../utils/formatDate';
import EmailButton from './ProcessedTimesheetHeader/EmailButton';
import DateDescription from './ProcessedTimesheetHeader/DateDescription';
import ButtonWithDropdownMenu from '../../../Shared/Components/ButtonWithDropdownMenu';
import { getSalesforceBookingLink } from '../../../utils/helpers';
import useAdminAuthContext from '../../hooks/useAdminAuthContext';
import UpdateHeaderDatesModal from './UpdateHeaderDatesModal';
import UpdateAccountingCaseInput from './UpdateAcountingCaseInput';
import { useToast } from '../../Contexts/ToastContext';

const styles = () => ({
    wrapper: { position: 'relative' },
    leftContainer: {
        padding: '16px',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    rightContainer: {
        padding: '12px 16px',
    },
    title: {
        fontSize: '22px',
        lineHeight: '120%',
        color: COLORS.LT_JOURNEY,
    },
    facilityName: {
        fontSize: '12px',
        lineHeight: '25px',
        color: COLORS.LT_MIDNIGHT,
    },
    linkButton: {
        width: 'fit-content',
        minWidth: 'fit-content',
        gap: 8,
        alignItems: 'center',
        padding: 0,
        fontSize: 12,
        textDecoration: 'underline',
        '&:hover, &:active': {
            textDecoration: 'underline',
        },
    },
    statusLabel: {
        fontSize: 9,
        fontWeight: 600,
        lineHeight: '100%',
        color: COLORS.LT_MIDNIGHT50,
        marginBottom: '5px',
    },
});

const ProcessedTimesheetHeader = ({ className, classes, data, loading, readOnly = false }) => {
    const isLg = useMediaQuery((theme) => theme.breakpoints.up('lg'));
    const { showSuccessToast } = useToast();
    const { isTimesheetSystemAdminUser } = useAdminAuthContext();
    const [openUpdateHeaderModal, setOpenUpdateHeaderModal] = useState(false);

    const handleHeaderInfoUpdateSuccess = () => {
        setOpenUpdateHeaderModal(false);
        showSuccessToast('Processed Timesheet dates successfully updated!');
    };

    return (
        <Box className={`${className} ${classes.wrapper}`}>
            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        minHeight: '195px',
                    }}
                >
                    <CircularProgress color="primary" />
                </Box>
            ) : (
                <>
                    <Grid container sx={{ minHeight: 196 }}>
                        <Grid
                            container
                            size={{ xs: 12, lg: 3.4 }}
                            className={classes.leftContainer}
                        >
                            <Typography component="h1" variant="h4" className={classes.title}>
                                {data?.clinicianName}
                            </Typography>

                            <Stack gap={1} my={1} width="100%">
                                <Typography className={classes.facilityName}>
                                    {data?.accountName}
                                </Typography>

                                <Button
                                    className={classes.linkButton}
                                    href={
                                        data?.bookingSfId
                                            ? getSalesforceBookingLink(data.bookingSfId)
                                            : undefined
                                    }
                                    target="_blank"
                                >
                                    BKG-{data?.bookingId}
                                    <LaunchIcon sx={{ fontSize: 17.5 }} />
                                </Button>

                                <UpdateAccountingCaseInput
                                    processedTimesheetId={data?.id}
                                    initialValue={data?.accountingCase}
                                    lastModified={data?.lastModified}
                                />
                            </Stack>

                            <ButtonWithDropdownMenu
                                label="Additional Details"
                                buttonProps={{ fullWidth: isLg, sx: { mt: 'auto' } }}
                                menuProps={{
                                    MenuListProps: {
                                        'aria-labelledby': 'Additional Details',
                                        sx: {
                                            padding: 0,
                                            '& .MuiTypography-root': {
                                                fontSize: '12px',
                                                lineHeight: '20px',
                                            },
                                        },
                                    },
                                    sx: {
                                        '& .MuiPaper-root': {
                                            maxWidth: '380px',
                                            minWidth: '250px',
                                        },
                                    },
                                }}
                                dropdownContent={
                                    <Stack
                                        divider={<Divider />}
                                        sx={{ color: COLORS.LT_MIDNIGHT, gap: 1.5, p: 1.5 }}
                                    >
                                        <div>
                                            <Typography fontWeight="bolder">Division:</Typography>
                                            <Typography fontWeight="normal">
                                                {data?.division}
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography fontWeight="bold">
                                                Number of Work Locations:
                                                <Typography component="span" fontWeight="normal">
                                                    &nbsp;{data?.workLocationCount}
                                                </Typography>
                                            </Typography>
                                            <Typography fontWeight="bold">
                                                Booking Work Location:
                                                <Typography component="span" fontWeight="normal">
                                                    &nbsp;{data?.bookingWorkLocation}
                                                </Typography>
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography fontWeight="bold">
                                                Preferred Payment Method:
                                                <Typography component="span" fontWeight="normal">
                                                    &nbsp;
                                                    {data?.preferredPaymentMethod ?? 'Unknown'}
                                                </Typography>
                                            </Typography>
                                            {data?.preferredPaymentMethod === 'Direct Deposit' && (
                                                <Typography fontWeight="bold">
                                                    {data?.preferredPaymentMethod ??
                                                        'Payment Method'}{' '}
                                                    Setup Date:
                                                    <Typography
                                                        component="span"
                                                        fontWeight="normal"
                                                    >
                                                        &nbsp;
                                                        {formatUtcDate(
                                                            data?.paymentSetupDate,
                                                            DATE_FORMATS.US_DATE
                                                        )}
                                                    </Typography>
                                                </Typography>
                                            )}
                                        </div>
                                        {data?.aB5 && (
                                            <Typography fontWeight="bold">
                                                AB5:
                                                <Typography component="span" fontWeight="normal">
                                                    &nbsp;W2
                                                </Typography>
                                            </Typography>
                                        )}
                                        {data?.specialProcessing && (
                                            <div>
                                                <Typography fontWeight="bold">
                                                    Special Processing:
                                                    <Typography
                                                        component="span"
                                                        fontWeight="normal"
                                                    >
                                                        &nbsp;{data.specialProcessing}
                                                    </Typography>
                                                </Typography>
                                            </div>
                                        )}
                                    </Stack>
                                }
                            />
                        </Grid>

                        {isLg && <Divider orientation="vertical" flexItem />}

                        <Grid
                            size={{ xs: 12, lg: 'grow' }}
                            className={classes.rightContainer}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-around',
                            }}
                        >
                            {!isLg && <Divider flexItem sx={{ mb: 1 }} />}
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                flexDirection={{ xs: 'column', sm: 'row' }}
                            >
                                <Box flexGrow={2}>
                                    <EmailButton
                                        title="Recruiter"
                                        name={data?.recruiter?.name}
                                        email={data?.recruiter?.email}
                                    />
                                </Box>
                                <Box flexGrow={2}>
                                    <EmailButton
                                        title="Sales Contact"
                                        name={data?.salesContact?.name}
                                        email={data?.salesContact?.email}
                                    />
                                </Box>
                                <Box
                                    display="flex"
                                    height="100%"
                                    justifyContent="center"
                                    alignItems="center"
                                    flexGrow={2}
                                >
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ display: { xs: 'none', sm: 'block' } }}
                                    />
                                    <Box sx={{ ml: 2, mr: 'auto' }}>
                                        <Typography className={classes.statusLabel}>
                                            STATUS
                                        </Typography>
                                        <UpdateStatusButton
                                            id={data?.id}
                                            status={data?.status}
                                            needsReviewStatus={data?.needsReviewStatus}
                                            clinicianName={data?.clinicianName}
                                            lastModified={data?.lastModified}
                                            isCorrection={data?.isCorrection}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Divider
                                sx={{ mt: 1, mb: 2, borderBottom: 'none', position: 'relative' }}
                            />

                            <Box position="relative" display="flex" flexDirection="column">
                                <Stack direction="column" spacing={0}>
                                    {isTimesheetSystemAdminUser && !readOnly && (
                                        <IconButton
                                            className={classes.editButton}
                                            color="contained-primary"
                                            onClick={() => setOpenUpdateHeaderModal(true)}
                                            title="Edit Dates"
                                            sx={{
                                                // position: 'absolute',
                                                top: -5,
                                                // right: 0,
                                                width: 24,
                                                height: 24,
                                                alignSelf: 'flex-end',
                                            }}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    )}
                                    <Stack
                                        direction="row"
                                        justifyContent="space-around"
                                        alignItems="center"
                                        divider={
                                            <Divider
                                                orientation="vertical"
                                                variant="fullWidth"
                                                flexItem
                                                sx={{ display: { xs: 'none', sm: 'block' } }}
                                            />
                                        }
                                        useFlexGap
                                        sx={{ flexWrap: 'wrap' }}
                                    >
                                        <DateDescription
                                            value={data?.originalPayDate}
                                            title="Original Pay"
                                            isUtc
                                        />
                                        <DateDescription
                                            value={data?.payrollDate}
                                            title="Payroll"
                                            isUtc
                                        />
                                        <DateDescription
                                            value={data?.submittedDate}
                                            title="Submitted"
                                            showTime
                                        />
                                        <DateDescription
                                            value={data?.approvalDate}
                                            title="Approval"
                                            showTime
                                        />
                                        <DateDescription
                                            value={data?.weekEndingDate}
                                            title="Week Ending"
                                            isUtc
                                        />
                                        <DateDescription
                                            value={data?.monthPostedDate}
                                            title="Month Posted"
                                            isUtc
                                        />
                                    </Stack>
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>

                    {openUpdateHeaderModal && isTimesheetSystemAdminUser && (
                        <UpdateHeaderDatesModal
                            data={data}
                            onSuccess={handleHeaderInfoUpdateSuccess}
                            onClose={() => setOpenUpdateHeaderModal(false)}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default withStyles(styles)(ProcessedTimesheetHeader);
